import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { get, getOr, map, omit, isEmpty, includes } from 'lodash/fp';
import { Content, ButtonToolbar, Button, Icon, Whisper, Popover } from 'rsuite';
import styled from 'styled-components';
import PageLayout from '../../../components/reservations/PageLayout';
import PageHeader from '../../../components/PageHeader';
import ContentLoader from '../../../components/ContentLoader';
import {
  fetchReservationForView,
  updateReservationStatus,
  unlinkReservation,
  fetchReservationPicklists
} from '../../../redux/Reservation/sagas/routines';
import {
  getHasLinkedOrderItems,
  getIsFetching,
  getIsSaving,
  getViewReservationData
} from '../../../redux/Reservation/selectors/routes/view';
import { getPicklists } from '../../../redux/Reservation/selectors/routes/orders';
import { clearReservationView } from '../../../redux/Reservation/actions/routes/view';
import reservationAddressLine from '../../../helpers/reservationAddressLine';
import {
  PENDING,
  REJECTED,
  CANCELED,
  COMPLETED
} from '../../../constants/reservation/Status';
import StatusLabels from '../../../constants/reservation/StatusLabels';
import {
  APPROVE,
  REJECT,
  CANCEL
} from '../../../constants/reservation/StatusActions';
import {
  createVisitUrl,
  createLinkGuestUrl,
  createReservationUrl
} from '../../../helpers/reservationUrls';
import { reservationsEditPath, reservationsOrdersPath } from '../../Paths';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import history from '../../history';
import redirectTo from '../../../helpers/redirectTo';
import {
  CREATE_VISIT as CREATE_VISIT_PERMISSION,
  CREATE_VISIT_PRE_POST as CREATE_VISIT_PRE_POST_PERMISSION
} from '../../../constants/permissions';
import * as PERMISSIONS from '../../../constants/permissions/reservation';
import {
  AllowCancelStatus,
  AllowCreateVisitStatus
} from '../../../constants/reservation/AllowActionSettings';
import {
  viewAccess,
  modifyAccess,
  fullAccess,
  hasViewAccess
} from '../../../helpers/permission';
import { parseInt } from '../../../helpers/utils';
import { hasPortalFeature } from '../../../helpers/config';
import { VIEW_ADDRESS } from '../../../constants/permissions/guest';
import { getMainSettingsData } from '../../../redux/Settings/selectors/main';
import { fetchClientColumns } from '../../../redux/sagas/client/routines';
import { getClientActiveColumns } from '../../../redux/selectors/client';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import NotifyGuestConfirmationModal from '../../../components/reservations/NotifyGuestConfirmationModal';
import CancelReservationConfirmationModal from '../../../components/reservations/CancelReservationConfirmationModal';
import AccessDenied from '../../../components/AccessDenied';
import AccessControl from '../../../components/AccessControl';
import OrderList from '../../../components/reservations/OrderList';
import PrintPdfButton from '../../../components/PrintPdfButton';
import ReservationDetailsPdf from '../../../components/reservations/ReservationDetailsPdf';
import GuestDetails from '../../../components/reservations/GuestDetails';
import RedLabel from '../../../components/RedLabel';
import SmtpAccountDisconnectedMessage from '../../../components/SmtpAccountDisconnectedMessage';
import { useSmtpAccounts } from '../../../hooks/useSmtpAccounts';

const currentDate = moment().format('YYYY-MM-DD');

const DetailsItem = styled.div`
  font-size: 16px;
  margin-bottom: 20px;
  > span {
    width: 180px;
    color: #999;
    display: inline-block;
  }
`;

const ContactNumber = styled.div`
  display: inline-block;
  margin-right: 10px;
  > small {
    color: #999;
  }
`;

const OrderListWrapper = styled.div`
  width: 85%;
  left: 180px;
  margin-top: -20px;
  position: relative;
  > .rs-panel {
    margin-bottom: 20px;
  }
`;

export default () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [emailInput, setEmailInput] = useState('');
  const [statusAction, setStatusAction] = useState(null);
  const [showNotifyGuestDialog, setShowNotifyGuestDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const loading = useSelector(getIsFetching);
  const isSaving = useSelector(getIsSaving);
  const reservation = useSelector(getViewReservationData);
  const reservationPicklists = useSelector(getPicklists);
  const hasLinkedOrderItems = useSelector(getHasLinkedOrderItems);
  const settings = useSelector(getMainSettingsData);
  const guestFields = useSelector(getClientActiveColumns);
  const permissions = useSelector(getUserPermissionsAsMap);
  const {
    hasActive: hasActiveSmtpAccount,
    showDisconnectedMessage
  } = useSmtpAccounts();
  const showAddress = get('reservationShowAddress', settings) === '1';
  const showCounty = get('reservationShowCounty', settings) === '1';
  const showHouseholdTotal =
    get('reservationShowHouseholdTotal', settings) === '1';
  const showEmail = get('reservationShowEmail', settings) === '1';
  const showHomePhone = get('reservationShowHomePhone', settings) === '1';
  const showWorkPhone = get('reservationShowWorkPhone', settings) === '1';
  const showCellPhone = get('reservationShowCellPhone', settings) === '1';
  const showNote = get('reservationShowNote', settings) === '1';
  const showGuestEmail = parseInt(getOr(0, 'guestRequireEmail', settings)) > 0;
  const showGuestHomePhone = get('guestShowHomePhone', settings) === '1';
  const showGuestWorkPhone = get('guestShowWorkPhone', settings) === '1';
  const showGuestCellPhone = get('guestShowCellPhone', settings) === '1';
  const showAgeBreakdown =
    get('guestOtherAgeHH', settings) === '1' &&
    get('guestAdditionalOthersAtHome', settings) !== '0';
  const isNotifyGuestEnabled = hasPortalFeature;

  useEffect(
    () => {
      dispatch(fetchReservationForView(id));
      dispatch(fetchReservationPicklists(id));
      dispatch(fetchClientColumns());
      return function reset() {
        dispatch(clearReservationView());
      };
    },
    [id]
  );

  const guestId = get('guestId', reservation);
  const guest = get('guest', reservation);
  const homeless = get('homeless', reservation);
  const email = get('email', reservation);
  const homePhone = get('homePhone', reservation);
  const workPhone = get('workPhone', reservation);
  const cellPhone = get('cellPhone', reservation);
  const addressLines = !isEmpty(reservation)
    ? reservationAddressLine(reservation, true, showCounty)
    : '';
  const status = get('status', reservation);
  const barcode = get('code', reservation);
  const requestTime = get('requestTime', reservation);
  const programs = map('name', get('programs', reservation));
  const note = get('note', reservation);
  const orders = getOr([], 'orders', reservation);
  const isPastReservation = moment(requestTime).isBefore(currentDate);
  const isTodayReservation = moment(requestTime).isSame(currentDate, 'day');
  const createVisitPermissions = !isTodayReservation
    ? [
        modifyAccess(CREATE_VISIT_PERMISSION),
        viewAccess(CREATE_VISIT_PRE_POST_PERMISSION)
      ]
    : [modifyAccess(CREATE_VISIT_PERMISSION)];
  const isCompleted = status === COMPLETED;
  const isNotRejectedOrCanceled = !includes(status, [REJECTED, CANCELED]);
  const allowManageOrders =
    isNotRejectedOrCanceled && !(isCompleted && hasLinkedOrderItems);

  const updateStatus = (notifyGuest = false) => {
    const body = { notifyGuest, email: emailInput };
    if (statusAction === CANCEL) body.cancellationReason = cancellationReason;
    dispatch(updateReservationStatus({ id, body, statusAction }));
    setStatusAction(null);
    setCancellationReason('');
    setShowNotifyGuestDialog(false);
  };

  const handleConfirmNotifyGuestDialog = () => {
    const notifyGuest = true;
    updateStatus(notifyGuest);
  };

  const handleSkipNotifyGuestDialog = () => {
    const notifyGuest = false;
    updateStatus(notifyGuest);
  };

  const handleCancelNotifyGuestDialog = () => {
    setShowNotifyGuestDialog(false);
  };

  const handleChangeDialogEmail = value => {
    setEmailInput(value);
  };

  const handleApprove = () => {
    if (isNotifyGuestEnabled && hasActiveSmtpAccount) {
      setStatusAction(APPROVE);
      setShowNotifyGuestDialog(true);
    } else {
      dispatch(updateReservationStatus({ id, statusAction: APPROVE }));
    }
  };

  const handleReject = () => {
    if (isNotifyGuestEnabled && hasActiveSmtpAccount) {
      setStatusAction(REJECT);
      setShowNotifyGuestDialog(true);
    } else {
      dispatch(updateReservationStatus({ id, statusAction: REJECT }));
    }
  };

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setShowCancelDialog(false);
  };

  const handleConfirmCancelDialog = reason => {
    setCancellationReason(reason);
    setShowCancelDialog(false);
    if (isNotifyGuestEnabled && hasActiveSmtpAccount) {
      setStatusAction(CANCEL);
      setShowNotifyGuestDialog(true);
      return;
    }
    dispatch(
      updateReservationStatus({
        id,
        body: { cancellationReason: reason },
        statusAction: CANCEL
      })
    );
  };

  const handleLinkGuest = () => {
    history.push(createLinkGuestUrl(reservation));
  };

  const handleUnLinkGuest = () => {
    const body = omit(['id'], reservation);
    dispatch(unlinkReservation({ id, body }));
  };

  const handleCreateReservation = () => {
    history.push(createReservationUrl(reservation));
  };

  const handleCreateVisit = () => {
    redirectTo(createVisitUrl(reservation), true);
  };

  const handleEdit = () => {
    history.push(pagePathToUrl(reservationsEditPath, { id }));
  };

  const handleOrders = () => {
    history.push(pagePathToUrl(reservationsOrdersPath, { id }));
  };

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={viewAccess(PERMISSIONS.MODULE)}
        fallback={<AccessDenied />}
      >
        <PageHeader title="Reservation Details" />
        <Content style={{ marginTop: 0 }}>
          {showDisconnectedMessage && <SmtpAccountDisconnectedMessage />}
          {loading || !reservation ? (
            <ContentLoader />
          ) : (
            <div>
              <DetailsItem>
                <span>Reservation: </span>
                {reservation.id}
              </DetailsItem>
              <DetailsItem>
                <span>Status: </span>
                <strong>{StatusLabels[status]}</strong>
              </DetailsItem>
              <DetailsItem>
                <span>Date: </span>
                {moment(get('requestTime', reservation)).format(
                  'dddd, MMM DD, YYYY'
                )}
              </DetailsItem>
              <DetailsItem>
                <span>Time: </span>
                {moment(get('requestTime', reservation)).format('LT')}
              </DetailsItem>
              {guest && (
                <DetailsItem>
                  <span>Linked Guest: </span>
                  <Whisper
                    placement="rightEnd"
                    speaker={
                      <Popover>
                        <GuestDetails
                          guest={guest}
                          showCounty={showCounty}
                          showEmail={showGuestEmail}
                          showHomePhone={showGuestHomePhone}
                          showCellPhone={showGuestCellPhone}
                          showWorkPhone={showGuestWorkPhone}
                          showAgeBreakdown={showAgeBreakdown}
                        />
                      </Popover>
                    }
                    trigger="click"
                  >
                    <Button appearance="link" size="lg" style={{ padding: 0 }}>
                      {get('firstname', guest)} {get('lastname', guest)}{' '}
                      <Icon icon="external-link-square" />
                    </Button>
                  </Whisper>
                </DetailsItem>
              )}

              <DetailsItem>
                <span>Name: </span>
                {get('firstName', reservation)} {get('lastName', reservation)}
              </DetailsItem>
              {homeless ? (
                <DetailsItem>
                  <span>Homeless: </span>
                  <RedLabel>Yes</RedLabel>
                </DetailsItem>
              ) : (
                showAddress && (
                  <DetailsItem>
                    <span>Address: </span>
                    {homeless ? (
                      'Homeless'
                    ) : (
                      <Fragment>
                        <AccessControl allowOnlyFor={viewAccess(VIEW_ADDRESS)}>
                          {addressLines.line1}
                          {`, `}
                        </AccessControl>
                        {addressLines.line2}
                      </Fragment>
                    )}
                  </DetailsItem>
                )
              )}
              {(showHomePhone || showWorkPhone || showCellPhone) && (
                <DetailsItem>
                  <span>Contact Number(s): </span>
                  {!homePhone && !workPhone && !cellPhone && 'None'}
                  {showHomePhone &&
                    homePhone && (
                      <ContactNumber>
                        <small>Home: </small>
                        {homePhone}
                      </ContactNumber>
                    )}
                  {showWorkPhone &&
                    workPhone && (
                      <ContactNumber>
                        <small>Work: </small>
                        {workPhone}
                      </ContactNumber>
                    )}
                  {showCellPhone &&
                    cellPhone && (
                      <ContactNumber>
                        <small>Cell: </small>
                        {cellPhone}
                      </ContactNumber>
                    )}
                </DetailsItem>
              )}
              {showEmail &&
                email && (
                  <DetailsItem>
                    <span>Email: </span>
                    {email}
                  </DetailsItem>
                )}
              {showHouseholdTotal && (
                <Fragment>
                  <DetailsItem>
                    <span>Total Individual: </span>
                    {get('householdTotal', reservation)} person(s)
                  </DetailsItem>
                </Fragment>
              )}
              <DetailsItem>
                <span>Help Desired: </span>
                {programs.join(', ')}
              </DetailsItem>
              <DetailsItem>
                <span>Outreach: </span>
                {get('outreach.name', reservation)}
              </DetailsItem>
              {(status === CANCELED || (showNote && note)) && (
                <DetailsItem>
                  <span>Notes: </span>
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      display: 'inline-block',
                      verticalAlign: 'top'
                    }}
                  >
                    {note}
                  </div>
                </DetailsItem>
              )}
              {barcode && (
                <DetailsItem>
                  <span>Barcode: </span>
                  {barcode}
                </DetailsItem>
              )}
              {!isEmpty(orders) && (
                <DetailsItem>
                  <span>Ordered Items: </span>
                  <OrderListWrapper>
                    {map(
                      order => (
                        <OrderList
                          key={`order-${order.id}`}
                          picklistName={order.picklistSnapshot.name}
                          items={order.items}
                          notes={order.notes}
                        />
                      ),
                      orders
                    )}
                  </OrderListWrapper>
                </DetailsItem>
              )}
              <ButtonToolbar>
                {!isPastReservation &&
                  status === PENDING && (
                    <AccessControl
                      allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}
                    >
                      <Button
                        onClick={handleApprove}
                        color="green"
                        disabled={isSaving}
                      >
                        <Icon icon="thumbs-up" />{' '}
                        {isSaving ? 'Loading...' : 'Approve'}
                      </Button>
                    </AccessControl>
                  )}

                {!isPastReservation &&
                  status === PENDING && (
                    <AccessControl
                      allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}
                    >
                      <Button
                        onClick={handleReject}
                        color="red"
                        disabled={isSaving}
                      >
                        <Icon icon="thumbs-down" />{' '}
                        {isSaving ? 'Loading...' : 'Reject'}
                      </Button>
                    </AccessControl>
                  )}
                {!isPastReservation &&
                  includes(status, AllowCreateVisitStatus) && (
                    <AccessControl allowWhenAll={createVisitPermissions}>
                      <Button
                        disabled={isSaving}
                        appearance="primary"
                        onClick={handleCreateVisit}
                      >
                        <Icon icon="shopping-bag" />
                        {!isCompleted ? ' Create' : ' View'} Visit
                      </Button>
                    </AccessControl>
                  )}
                <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}>
                  <Button
                    disabled={isSaving}
                    appearance="primary"
                    onClick={handleCreateReservation}
                  >
                    <Icon icon="calendar-plus-o" /> Create Reservation
                  </Button>
                  {guestId ? (
                    <Button
                      disabled={isSaving || isCompleted}
                      appearance="primary"
                      onClick={handleUnLinkGuest}
                    >
                      <Icon icon="unlink" /> Unlink Guest
                    </Button>
                  ) : (
                    <Button
                      disabled={isSaving}
                      appearance="primary"
                      onClick={handleLinkGuest}
                    >
                      <Icon icon="link" /> Link Guest
                    </Button>
                  )}

                  <Button
                    disabled={isSaving || isCompleted}
                    appearance="primary"
                    onClick={handleEdit}
                  >
                    <Icon icon="edit2" /> Edit
                  </Button>
                  {!isEmpty(reservationPicklists) && (
                    <Button
                      disabled={isSaving || !allowManageOrders}
                      appearance="primary"
                      onClick={handleOrders}
                    >
                      <Icon icon="edit2" /> Manage Orders
                    </Button>
                  )}

                  <PrintPdfButton
                    fileName="reservation"
                    content={
                      <ReservationDetailsPdf
                        data={reservation}
                        guestFields={guestFields}
                        showLine1Address={hasViewAccess(
                          VIEW_ADDRESS,
                          permissions
                        )}
                      />
                    }
                  />
                </AccessControl>
                <AccessControl allowOnlyFor={fullAccess(PERMISSIONS.MODULE)}>
                  {!isPastReservation &&
                    includes(status, AllowCancelStatus) && (
                      <Button
                        onClick={handleCancel}
                        color="red"
                        disabled={isSaving}
                      >
                        <Icon icon="close-circle" />{' '}
                        {isSaving ? 'Loading...' : 'Cancel'}
                      </Button>
                    )}
                </AccessControl>
              </ButtonToolbar>
            </div>
          )}
          {hasActiveSmtpAccount && (
            <NotifyGuestConfirmationModal
              show={showNotifyGuestDialog}
              email={email || getOr('', 'email', guest)}
              showEmailField
              onConfirm={handleConfirmNotifyGuestDialog}
              onSkip={handleSkipNotifyGuestDialog}
              onCancel={handleCancelNotifyGuestDialog}
              onChangeEmail={handleChangeDialogEmail}
            />
          )}
          <CancelReservationConfirmationModal
            show={showCancelDialog}
            onCancel={handleCloseCancelDialog}
            onConfirm={handleConfirmCancelDialog}
          />
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
